/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Signals API
 * API for managing signals (signaleringen) within an organization. Signals are automated notifications triggered by conditions defined in signal definitions. All operations are scoped to the authenticated user\'s organization. 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Request to create a new custom signal definition
 */
export interface CreateSignalDefinitionRequest {
    /**
     * Human-readable name of the signal definition
     */
    'name': string;
    /**
     * Optional description of what this signal detects
     */
    'description'?: string;
    'rootEntityType': SignalRootEntity;
    /**
     * Template for rendering signal messages with placeholders
     */
    'messageTemplate': string;
    /**
     * JSON expression defining the rule logic
     */
    'ruleExpression': string;
    /**
     * Optional field key signals from this definition deep-link to (#3166), e.g. `bsn`. Omitted or empty means page-level navigation. 
     */
    'targetAnchor'?: string;
}


/**
 * Consolidated dashboard payload bundling every signal bucket the dashboard renders. Returned by `GET /api/v1/signals/dashboard` so the dashboard can load all buckets in one request instead of one request per bucket. 
 */
export interface DashboardSignalsResponse {
    'myClients': MyClientSignalPage;
    'personalPractitioner': SignalPage;
    'billingAsPractitioner': MyClientSignalPage;
    /**
     * Organization-level (catch-all) signals. Present only for org admins (`organization_settings.update`); omitted for non-admins. 
     */
    'organizationLevel'?: SignalPage;
    'visibleToMe': SignalPage;
}
/**
 * Schema describing available entities and their relationships for condition building
 */
export interface EntityGraphSchema {
    /**
     * List of available entity types
     */
    'entities': Array<EntitySchema>;
    /**
     * The conditions every rule is evaluated under, on top of what the rule itself says. A definition\'s `ruleExpression` is not its whole matching predicate: the evaluator ANDs it with organization scoping and with these guards, so a rule read on its own states a wider set of matches than can ever occur. Render them beside any rule that is shown or authored, filtered by the rule\'s own `rootEntityType`. 
     */
    'structuralGuards': Array<StructuralGuardSchema>;
}
/**
 * Schema for a single entity type
 */
export interface EntitySchema {
    /**
     * Technical name of the entity
     */
    'name': string;
    /**
     * Human-readable name of the entity
     */
    'displayName': string;
    /**
     * What the entity represents, in domain terms
     */
    'description': string;
    /**
     * Fields available on this entity
     */
    'fields': Array<FieldSchema>;
    /**
     * Relations to other entities
     */
    'relations'?: Array<RelationSchema>;
}
/**
 * Schema for a single field on an entity
 */
export interface FieldSchema {
    /**
     * Technical name of the field
     */
    'name': string;
    /**
     * Human-readable name of the field
     */
    'displayName': string;
    /**
     * What the field means, in domain terms
     */
    'description': string;
    'type': FieldType;
    /**
     * Whether the field can be null
     */
    'nullable'?: boolean;
    /**
     * If type is ENUM, the possible values
     */
    'enumValues'?: Array<string>;
    /**
     * Present only on an ENUM whose values are the caller\'s organization\'s own records (`clientState`, the organization\'s cliëntstatussen). One entry per value in `enumValues`, naming it; a rule stores `value` and a person reads `label`. An inactive value stays legal in an existing rule but should not be offered for a new one. 
     */
    'enumOptions'?: Array<SignalFieldEnumOption>;
}


/**
 * The data type of a field
 */

export const FieldType = {
    String: 'STRING',
    Integer: 'INTEGER',
    Decimal: 'DECIMAL',
    Boolean: 'BOOLEAN',
    Date: 'DATE',
    Datetime: 'DATETIME',
    Enum: 'ENUM',
    Uuid: 'UUID'
} as const;

export type FieldType = typeof FieldType[keyof typeof FieldType];


/**
 * A natural-language description of the signal an author wants to create
 */
export interface GenerateSignalDraftRequest {
    /**
     * Plain-language description of the desired signal, e.g. \"waarschuw als een cliënt geen BSN heeft\". 
     */
    'description': string;
}
/**
 * A signal for a client where the current user is a behandelaar
 */
export interface MyClientSignalDTO {
    /**
     * Unique identifier for this signal instance
     */
    'id': string;
    /**
     * The key of the signal definition that triggered this signal
     */
    'definitionKey': string;
    /**
     * Human-readable name of the signal definition (used for grouping)
     */
    'definitionName': string;
    /**
     * Rendered message with placeholders filled in
     */
    'message': string;
    'severity': SignalSeverity;
    'rootEntityType': SignalRootEntity;
    /**
     * ID of the root entity this signal relates to
     */
    'rootEntityId': string;
    /**
     * ID of the client this signal relates to (for navigation)
     */
    'clientId': string;
    /**
     * Full name of the client (for display without additional fetch)
     */
    'clientName': string;
    /**
     * ID of the appointment behind the signal, when it has one (APPOINTMENT-rooted, or APPOINTMENT_FACT-rooted via the fact). Navigation to the appointment page must use this — a fact signal\'s rootEntityId is the per-fact fact_uid, not an appointment UUID (#3320). 
     */
    'appointmentId'?: string;
    /**
     * ID of the zorgtraject the consult behind an APPOINTMENT_FACT-rooted signal falls under. The flat counterpart of `SignalNavigationContext.careTrajectoryId`. 
     */
    'careTrajectoryId'?: string;
    /**
     * Field key the signal deep-links to, authored on its definition (#3166). The flat counterpart of `SignalNavigationContext.targetAnchor` — this DTO carries its navigation data at the top level. 
     */
    'targetAnchor'?: string;
    /**
     * Unix timestamp (milliseconds) when this signal was created
     */
    'createdAt': number;
}


/**
 * Paginated list of signals for my clients
 */
export interface MyClientSignalPage {
    /**
     * List of signals on this page
     */
    'content': Array<MyClientSignalDTO>;
    /**
     * Total number of signals matching the query
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
    /**
     * Current page number (0-based)
     */
    'number': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Whether this is the first page
     */
    'first'?: boolean;
    /**
     * Whether this is the last page
     */
    'last'?: boolean;
    /**
     * Whether this page has no content
     */
    'empty'?: boolean;
}
/**
 * RFC 7807 Problem Details response
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary of the problem
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation specific to this occurrence
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * The cardinality of a relation
 */

export const RelationCardinality = {
    OneToOne: 'ONE_TO_ONE',
    OneToMany: 'ONE_TO_MANY',
    ManyToOne: 'MANY_TO_ONE',
    ManyToMany: 'MANY_TO_MANY'
} as const;

export type RelationCardinality = typeof RelationCardinality[keyof typeof RelationCardinality];


/**
 * Schema for a relation to another entity
 */
export interface RelationSchema {
    /**
     * Technical name of the relation
     */
    'name': string;
    /**
     * Human-readable name of the relation
     */
    'displayName': string;
    /**
     * The entity type this relation points to
     */
    'targetEntity': string;
    'cardinality': RelationCardinality;
}


/**
 * A single reason a candidate rule expression was rejected
 */
export interface RuleValidationError {
    /**
     * Where in the expression the problem sits, as a JSON path
     */
    'path': string;
    'code': RuleValidationErrorCode;
    /**
     * Human-readable explanation, naming the offending token and — where a closed set applies — the legal alternatives. 
     */
    'message': string;
}


/**
 * The kind of problem a rule validation error describes
 */

export const RuleValidationErrorCode = {
    MalformedExpression: 'MALFORMED_EXPRESSION',
    UnknownRoot: 'UNKNOWN_ROOT',
    UnknownField: 'UNKNOWN_FIELD',
    UnknownRelation: 'UNKNOWN_RELATION',
    InvalidPathCardinality: 'INVALID_PATH_CARDINALITY',
    IllegalEnumValue: 'ILLEGAL_ENUM_VALUE',
    IncompatibleOperator: 'INCOMPATIBLE_OPERATOR',
    IncompatibleValue: 'INCOMPATIBLE_VALUE',
    UnsupportedExistsTarget: 'UNSUPPORTED_EXISTS_TARGET',
    UnsupportedCondition: 'UNSUPPORTED_CONDITION'
} as const;

export type RuleValidationErrorCode = typeof RuleValidationErrorCode[keyof typeof RuleValidationErrorCode];


/**
 * Request to save an authored draft as a signal definition. The author supplies the human-facing name and message template (which the generator does not produce) and the rule to persist — either the generated one unchanged or an edited version. 
 */
export interface SaveSignalDraftRequest {
    /**
     * Human-readable name of the signal definition
     */
    'name': string;
    /**
     * Optional description of what this signal detects
     */
    'description'?: string;
    'rootEntityType': SignalRootEntity;
    /**
     * Template for rendering signal messages with placeholders
     */
    'messageTemplate': string;
    /**
     * JSON expression defining the rule logic to persist. Re-validated on save; an invalid rule is rejected with 422. 
     */
    'ruleExpression': string;
}


/**
 * A 422 problem returned when the submitted rule fails deterministic validation, extending RFC 7807 with the structured validation errors. 
 */
export interface SaveSignalDraftValidationProblem {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary of the problem
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation specific to this occurrence
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * One entry per validation problem with the submitted rule.
     */
    'errors'?: Array<RuleValidationError>;
}
/**
 * A signal instance triggered by a signal definition
 */
export interface SignalDTO {
    /**
     * Unique identifier for this signal instance
     */
    'id': string;
    /**
     * The key of the signal definition that triggered this signal
     */
    'definitionKey': string;
    /**
     * Human-readable name of the signal
     */
    'name': string;
    /**
     * Rendered message with placeholders filled in
     */
    'message': string;
    'severity': SignalSeverity;
    'rootEntityType': SignalRootEntity;
    /**
     * ID of the root entity this signal relates to
     */
    'rootEntityId': string;
    /**
     * Whether this signal blocks invoice creation and declaration export. Read off its definition, so it is what `POST /api/v1/signals/{signalId}/suppress` will decide by: a blocking signal can only be snoozed, never dismissed for good, and the UI has to know which of the two it is offering before the call is made. 
     */
    'blocksBilling': boolean;
    'navigationContext'?: SignalNavigationContext;
    'factContext'?: SignalFactContext;
    /**
     * Unix timestamp (milliseconds) when this signal was created
     */
    'createdAt': number;
}


/**
 * A signal definition that defines conditions for triggering signals
 */
export interface SignalDefinitionDTO {
    /**
     * Unique identifier for this signal definition
     */
    'id': string;
    /**
     * Unique key for this signal definition
     */
    'key': string;
    /**
     * Human-readable name of the signal definition
     */
    'name': string;
    /**
     * Optional description of what this signal detects
     */
    'description'?: string;
    'rootEntityType': SignalRootEntity;
    /**
     * Whether this signal definition is currently active
     */
    'enabled': boolean;
    /**
     * Number of currently active signals for this definition
     */
    'signalCount': number;
    /**
     * Template for rendering signal messages with placeholders
     */
    'messageTemplate'?: string;
    /**
     * JSON expression defining the rule logic (read-only)
     */
    'ruleExpression'?: string;
    /**
     * Whether this is a system-provided definition that cannot be deleted
     */
    'isSystem': boolean;
    /**
     * Whether an active signal from this definition holds back invoice creation and GDS801 export, as opposed to being a soft warning. It also decides which structural guards the rule is evaluated under, so it is needed to render the guards beside the rule honestly. 
     */
    'blocksBilling': boolean;
    /**
     * Field key signals from this definition deep-link to (#3166). Absent when the definition navigates to the page only. 
     */
    'targetAnchor'?: string;
}


/**
 * Outcome of turning a natural-language description into a candidate signal rule (ADR-034). Exactly one of three things happened, indicated by `status`, and none of them is an *invalid* rule. `attemptId` identifies the logged attempt and is used to save or discard it. 
 */
export interface SignalDraftDTO {
    /**
     * Id of the logged authoring attempt. Pass this to save or discard the draft. 
     */
    'attemptId': string;
    'status': SignalDraftStatus;
    /**
     * The root the candidate rule is anchored on. Present only when `DRAFTED`.
     */
    'rootEntityType'?: SignalRootEntity;
    /**
     * The validated candidate rule (signal DSL JSON). Present only when `DRAFTED`. 
     */
    'ruleExpression'?: string;
    /**
     * Every interpretation the model made — includes the chosen root and the reading of any loaded term — so the author can correct a wrong assumption by editing the description and regenerating. May be empty. 
     */
    'assumptions': Array<string>;
    /**
     * Why the request cannot be expressed with the current schema, in the model\'s words. Present only when `INEXPRESSIBLE`. 
     */
    'inexpressibleReason'?: string;
    /**
     * Why the attempt failed. Present only when `FAILED`.
     */
    'failureReason'?: SignalDraftFailureReason;
    /**
     * The validation errors from the last repair round. Present only when `FAILED` because validation could not be satisfied. 
     */
    'validationErrors'?: Array<RuleValidationError>;
}


/**
 * Why a draft attempt failed to yield a validated rule
 */

export const SignalDraftFailureReason = {
    UnparseableModelOutput: 'UNPARSEABLE_MODEL_OUTPUT',
    ValidationFailed: 'VALIDATION_FAILED'
} as const;

export type SignalDraftFailureReason = typeof SignalDraftFailureReason[keyof typeof SignalDraftFailureReason];


/**
 * The outcome of a draft attempt. `DRAFTED` carries a validated candidate rule; `INEXPRESSIBLE` means the request cannot be expressed with the current schema; `FAILED` means no valid rule could be produced within the bounded repair budget. 
 */

export const SignalDraftStatus = {
    Drafted: 'DRAFTED',
    Inexpressible: 'INEXPRESSIBLE',
    Failed: 'FAILED'
} as const;

export type SignalDraftStatus = typeof SignalDraftStatus[keyof typeof SignalDraftStatus];


/**
 * What the signal is about, read off the `appointment_fact` row it is rooted on. The fact grain is (appointment, client, practitioner), so an APPOINTMENT_FACT signal is about exactly one client and one session — which is the whole of what a group appointment cannot tell you from its appointment id alone (#3832).  Absent for every other root type: a CLIENT- or CARE_TRAJECTORY-rooted signal carries less, and the row renders what it has rather than empty fields. Absent too when the fact row no longer exists.  Deliberately carries no BSN, e-mail or date of birth. All three sit on the same fact row and none of them helps triage a signal; a signals list is a poor place to widen exposure of identifying data.  It carries no `clientId` either: `navigationContext.clientId` is resolved from this same fact row and is what routes the \"naar cliënt\" destination, so a second copy here would be one identifier with two spellings to keep in agreement. 
 */
export interface SignalFactContext {
    /**
     * Full name of the client the signal is about
     */
    'clientName'?: string;
    /**
     * Unix timestamp (milliseconds) the session was scheduled to start
     */
    'scheduledStartTime'?: number;
    /**
     * Scheduled length of the session in minutes
     */
    'scheduledDurationMinutes'?: number;
    /**
     * Name of the appointment type
     */
    'appointmentTypeName'?: string;
    /**
     * Category of the appointment type as stored on the fact row (e.g. BEHANDELING, VERBLIJF). Free-form on purpose — it is displayed, never branched on. 
     */
    'appointmentTypeCategory'?: string;
    /**
     * Full name of the behandelaar this fact row is for
     */
    'practitionerName'?: string;
    /**
     * Full name of the regiebehandelaar of the linked zorgtraject
     */
    'hoofdbehandelaarName'?: string;
    /**
     * Name of the location the session takes place at
     */
    'locationName'?: string;
    /**
     * Name of the client\'s insurer
     */
    'insurerName'?: string;
    /**
     * Name of the zorgtraject the session is linked to
     */
    'careTrajectoryName'?: string;
    /**
     * Zorgtrajectnummer of the linked zorgtraject, when it carries one
     */
    'zorgtrajectNummer'?: string;
}
/**
 * One value of an organization-specific ENUM field
 */
export interface SignalFieldEnumOption {
    /**
     * What a rule stores — for `clientState`, the client state\'s id
     */
    'value': string;
    /**
     * What a person reads — for `clientState`, the client state\'s name
     */
    'label': string;
    /**
     * Whether the value may be chosen for a new rule
     */
    'active': boolean;
}
/**
 * One signal definition\'s share of the filtered audience set
 */
export interface SignalGroupDTO {
    /**
     * The key of the signal definition. Pass it back as `definitionKey` on `/api/v1/signals/visible-to-me` to list exactly the signals counted here. 
     */
    'definitionKey': string;
    /**
     * Human-readable name of the signal definition
     */
    'name': string;
    /**
     * The definition\'s own description, where it has one
     */
    'description'?: string;
    'severity': SignalSeverity;
    'rootEntityType': SignalRootEntity;
    /**
     * Whether an active signal from this definition holds back invoice creation and GDS801 export, as opposed to being a soft warning. 
     */
    'blocksBilling': boolean;
    /**
     * Number of matching signals raised by this definition
     */
    'count': number;
}


/**
 * The filtered audience set counted per signal definition, plus the totals the counts reconcile against. 
 */
export interface SignalGroupSummary {
    /**
     * Total number of signals matching the filters — the same number `SignalPage.totalElements` carries for the same filters. Every signal falls under exactly one definition, so the group counts sum to this. 
     */
    'totalSignals': number;
    /**
     * One entry per signal definition with at least one matching signal
     */
    'groups': Array<SignalGroupDTO>;
}
/**
 * Additional context required for navigating to the root entity. Contains IDs and metadata needed to construct navigation links. For example, CareTrajectory navigation requires clientId. 
 */
export interface SignalNavigationContext {
    /**
     * ID of the client (required for CareTrajectory, Document, FormSubmission navigation)
     */
    'clientId'?: string;
    /**
     * ID of the appointment behind the signal. Set for APPOINTMENT-rooted signals and for APPOINTMENT_FACT-rooted signals (whose rootEntityId is the per-fact fact_uid, not an appointment UUID) — navigation to the appointment page must use this, never rootEntityId (#3320). 
     */
    'appointmentId'?: string;
    /**
     * ID of the zorgtraject the consult behind the signal falls under. Set for APPOINTMENT_FACT-rooted signals whose fact names one, so a signal about a zorgtraject field (GGZ-profiel, DSM-diagnose) can link to the zorgtraject rather than to the afspraak. Absent when the consult falls under none. 
     */
    'careTrajectoryId'?: string;
    /**
     * Field key the signal deep-links to, authored on its definition (#3166). The frontend appends it to the destination route as `?focus=<key>` and the target page focuses that control. Absent — or naming a field the destination page does not render — means page-level navigation, never an error. 
     */
    'targetAnchor'?: string;
}
/**
 * Paginated list of signals
 */
export interface SignalPage {
    /**
     * List of signals on this page
     */
    'content': Array<SignalDTO>;
    /**
     * Total number of signals matching the query
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
    /**
     * Current page number (0-based)
     */
    'number': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Whether this is the first page
     */
    'first'?: boolean;
    /**
     * Whether this is the last page
     */
    'last'?: boolean;
    /**
     * Whether this page has no content
     */
    'empty'?: boolean;
}
/**
 * A single entity matched by a previewed rule
 */
export interface SignalPreviewMatch {
    /**
     * ID of the matched entity
     */
    'id': string;
    /**
     * Human-readable label for the matched entity (e.g. a client\'s name), where one is available for the root entity type. 
     */
    'label'?: string;
}
/**
 * A candidate signal rule to preview against the organization\'s data
 */
export interface SignalPreviewRequest {
    'rootEntityType': SignalRootEntity;
    /**
     * JSON expression defining the rule logic to preview
     */
    'ruleExpression': string;
}


/**
 * Outcome of previewing a candidate rule. When `valid` is true the rule was evaluated and `matchCount`/`sample` describe what it currently flags; when false the rule was rejected before evaluation and `errors` explains why. 
 */
export interface SignalPreviewResponse {
    /**
     * Whether the candidate rule is well-formed and was evaluated
     */
    'valid': boolean;
    /**
     * Number of entities in the organization the rule currently matches. Present only when `valid` is true. 
     */
    'matchCount'?: number;
    /**
     * A bounded sample of the matched entities (up to a fixed limit), each with a display label. Present only when `valid` is true. 
     */
    'sample'?: Array<SignalPreviewMatch>;
    /**
     * Whether `matchCount` exceeds the returned sample size (more matches exist than are shown). Present only when `valid` is true. 
     */
    'sampleTruncated'?: boolean;
    /**
     * Why the candidate was rejected, one entry per problem. Present only when `valid` is false. 
     */
    'errors'?: Array<RuleValidationError>;
}
/**
 * The type of entity that is the root of a signal
 */

export const SignalRootEntity = {
    Client: 'CLIENT',
    Appointment: 'APPOINTMENT',
    CareTrajectory: 'CARE_TRAJECTORY',
    Document: 'DOCUMENT',
    FormSubmission: 'FORM_SUBMISSION',
    Contract: 'CONTRACT',
    AppointmentFact: 'APPOINTMENT_FACT',
    Organization: 'ORGANIZATION',
    Location: 'LOCATION',
    Practitioner: 'PRACTITIONER',
    Insurer: 'INSURER'
} as const;

export type SignalRootEntity = typeof SignalRootEntity[keyof typeof SignalRootEntity];


/**
 * The severity level of a signal for visual hierarchy
 */

export const SignalSeverity = {
    Info: 'INFO',
    Warning: 'WARNING',
    Urgent: 'URGENT'
} as const;

export type SignalSeverity = typeof SignalSeverity[keyof typeof SignalSeverity];


/**
 * Identifies a structural guard. - `ACTIVE_CLIENT`: the owning client must not be archived or soft-deleted. - `BILLABLE_FACT`: only a completed appointment with a present client carries a signal. - `UNSETTLED_CONSULT`: a consult that has already been declared or invoiced carries none. - `IMPORTED_PAST_APPOINTMENT`: an appointment an import created, once it is in the past,   carries no signal from a definition that does not block a declaration. - `AUTHORED_FORM`: a submission of a system form (the dossiernotitie) is not a form   submission to any rule that asks about one. 
 */

export const SignalStructuralGuard = {
    ActiveClient: 'ACTIVE_CLIENT',
    BillableFact: 'BILLABLE_FACT',
    UnsettledConsult: 'UNSETTLED_CONSULT',
    ImportedPastAppointment: 'IMPORTED_PAST_APPOINTMENT',
    AuthoredForm: 'AUTHORED_FORM'
} as const;

export type SignalStructuralGuard = typeof SignalStructuralGuard[keyof typeof SignalStructuralGuard];


/**
 * One signal the organization has put aside, with the judgement behind it.  It carries the definition\'s own fields — key, name, severity, root type — plus the message the signal was showing, rendered from the context copied off the signal row before it was deleted. Nothing else can render it: the signal itself is gone. 
 */
export interface SignalSuppressionDTO {
    /**
     * Identifier of the suppression, used to lift it
     */
    'id': string;
    /**
     * Key of the definition whose signal was suppressed
     */
    'definitionKey': string;
    /**
     * Human-readable name of the signal
     */
    'name': string;
    /**
     * The message the signal was showing when it was put aside
     */
    'message': string;
    'severity': SignalSeverity;
    'rootEntityType': SignalRootEntity;
    /**
     * The entity half of the suppressed signal\'s identity
     */
    'rootEntityId': string;
    /**
     * Whether the suppressed signal\'s definition blocks billing. Such a suppression always carries an `expiresAt`; it is stated here so a reader can see why. 
     */
    'blocksBilling': boolean;
    /**
     * Why the practice put this signal aside
     */
    'reason': string;
    /**
     * Full name of the person who recorded the suppression. Absent when that user no longer has a membership the reader can resolve — the suppression itself still stands. 
     */
    'suppressedByName'?: string;
    /**
     * Unix timestamp (milliseconds) the suppression was recorded
     */
    'suppressedAt': number;
    /**
     * Unix timestamp (milliseconds) the suppression stops applying. Absent for a permanent one. 
     */
    'expiresAt'?: number;
}


/**
 * Paginated list of live signal suppressions
 */
export interface SignalSuppressionPage {
    /**
     * List of suppressions on this page
     */
    'content': Array<SignalSuppressionDTO>;
    /**
     * Total number of live suppressions in the organization
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
    /**
     * Current page number (0-based)
     */
    'number': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Whether this is the first page
     */
    'first'?: boolean;
    /**
     * Whether this is the last page
     */
    'last'?: boolean;
    /**
     * Whether this page has no content
     */
    'empty'?: boolean;
}
/**
 * A condition applied to every rule rooted on one of its `appliesTo` entities
 */
export interface StructuralGuardSchema {
    'key': SignalStructuralGuard;
    /**
     * Short human-readable title of the guard
     */
    'displayName': string;
    /**
     * What the guard admits, in domain terms
     */
    'description': string;
    /**
     * The root entity types this guard is applied to
     */
    'appliesTo': Array<SignalRootEntity>;
    /**
     * Whether the guard also binds a definition whose signals hold back a declaration (`blocksBilling`). A guard is applied when its `appliesTo` names the rule\'s root **and** this is true or the definition does not block, so a rule shown beside a guard that is false while it blocks would be showing a condition that is not applied to it. 
     */
    'appliesWhenBlocking': boolean;
}


/**
 * The judgement being recorded when a signal is put aside.  Neither property carries a `default:` — an OpenAPI default on a request-body property survives deserialisation and would silently rewrite what the caller sent. 
 */
export interface SuppressSignalRequest {
    /**
     * Why this signal is being put aside, in the practitioner\'s own words. Required: an org-wide dismissal that nobody can account for is what makes suppression a way to lose work rather than a way to manage it. 
     */
    'reason': string;
    /**
     * Unix timestamp (milliseconds) at which the suppression stops applying. Omit for a permanent suppression.  **Required, and at most 90 days out, when the signal\'s definition blocks billing.** Permanently suppressing a billing blocker hides money in the way that is found at year-end. When the snooze runs out the signal simply matches again on the next sweep. 
     */
    'expiresAt'?: number;
}
/**
 * Request to update a signal definition
 */
export interface UpdateSignalDefinitionRequest {
    /**
     * Whether this signal definition should be active
     */
    'enabled': boolean;
    /**
     * Field key signals from this definition deep-link to (#3166). Omit the property to leave the stored anchor untouched — the enabled toggle must not clear an authored anchor as a side effect. Send an empty string to remove it. 
     */
    'targetAnchor'?: string;
    /**
     * Whether an active signal from this definition holds back invoice creation and GDS801 export. Omit the property to leave it untouched. Only a custom definition\'s flag can be changed; changing a system definition\'s is a 400. 
     */
    'blocksBilling'?: boolean;
}

/**
 * SignalDefinitionsApi - axios parameter creator
 */
export const SignalDefinitionsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Create a new custom signal definition for the organization. Custom definitions are organization-scoped and can be deleted. A new definition is created disabled and non-billing-blocking: it matches nothing until it is switched on with `PUT /signal-definitions/{id}`, and holds back no declaratie until `blocksBilling` is set there separately. Use `POST /signals/preview` to see what the rule matches first. 
         * @summary Create signal definition
         * @param {CreateSignalDefinitionRequest} createSignalDefinitionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createSignalDefinition: async (createSignalDefinitionRequest: CreateSignalDefinitionRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createSignalDefinitionRequest' is not null or undefined
            assertParamExists('createSignalDefinition', 'createSignalDefinitionRequest', createSignalDefinitionRequest)
            const localVarPath = `/api/v1/signal-definitions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createSignalDefinitionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Delete a custom signal definition. System definitions (seeded by the platform) cannot be deleted. All associated signals will also be deleted. 
         * @summary Delete signal definition
         * @param {string} definitionId The ID of the signal definition to delete
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteSignalDefinition: async (definitionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'definitionId' is not null or undefined
            assertParamExists('deleteSignalDefinition', 'definitionId', definitionId)
            const localVarPath = `/api/v1/signal-definitions/{definitionId}`
                .replace(`{${"definitionId"}}`, encodeURIComponent(String(definitionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List signal definitions for the organization. This is an admin endpoint for managing signal definitions. 
         * @summary List signal definitions
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSignalDefinitions: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/signal-definitions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update a signal definition. Currently supports toggling the active/enabled status. 
         * @summary Update signal definition
         * @param {string} definitionId The ID of the signal definition to update
         * @param {UpdateSignalDefinitionRequest} updateSignalDefinitionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateSignalDefinition: async (definitionId: string, updateSignalDefinitionRequest: UpdateSignalDefinitionRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'definitionId' is not null or undefined
            assertParamExists('updateSignalDefinition', 'definitionId', definitionId)
            // verify required parameter 'updateSignalDefinitionRequest' is not null or undefined
            assertParamExists('updateSignalDefinition', 'updateSignalDefinitionRequest', updateSignalDefinitionRequest)
            const localVarPath = `/api/v1/signal-definitions/{definitionId}`
                .replace(`{${"definitionId"}}`, encodeURIComponent(String(definitionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateSignalDefinitionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SignalDefinitionsApi - functional programming interface
 */
export const SignalDefinitionsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SignalDefinitionsApiAxiosParamCreator(configuration)
    return {
        /**
         * Create a new custom signal definition for the organization. Custom definitions are organization-scoped and can be deleted. A new definition is created disabled and non-billing-blocking: it matches nothing until it is switched on with `PUT /signal-definitions/{id}`, and holds back no declaratie until `blocksBilling` is set there separately. Use `POST /signals/preview` to see what the rule matches first. 
         * @summary Create signal definition
         * @param {CreateSignalDefinitionRequest} createSignalDefinitionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createSignalDefinition(createSignalDefinitionRequest: CreateSignalDefinitionRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignalDefinitionDTO>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createSignalDefinition(createSignalDefinitionRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalDefinitionsApi.createSignalDefinition']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Delete a custom signal definition. System definitions (seeded by the platform) cannot be deleted. All associated signals will also be deleted. 
         * @summary Delete signal definition
         * @param {string} definitionId The ID of the signal definition to delete
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteSignalDefinition(definitionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteSignalDefinition(definitionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalDefinitionsApi.deleteSignalDefinition']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List signal definitions for the organization. This is an admin endpoint for managing signal definitions. 
         * @summary List signal definitions
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listSignalDefinitions(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<SignalDefinitionDTO>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listSignalDefinitions(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalDefinitionsApi.listSignalDefinitions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Update a signal definition. Currently supports toggling the active/enabled status. 
         * @summary Update signal definition
         * @param {string} definitionId The ID of the signal definition to update
         * @param {UpdateSignalDefinitionRequest} updateSignalDefinitionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateSignalDefinition(definitionId: string, updateSignalDefinitionRequest: UpdateSignalDefinitionRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignalDefinitionDTO>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateSignalDefinition(definitionId, updateSignalDefinitionRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalDefinitionsApi.updateSignalDefinition']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SignalDefinitionsApi - factory interface
 */
export const SignalDefinitionsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SignalDefinitionsApiFp(configuration)
    return {
        /**
         * Create a new custom signal definition for the organization. Custom definitions are organization-scoped and can be deleted. A new definition is created disabled and non-billing-blocking: it matches nothing until it is switched on with `PUT /signal-definitions/{id}`, and holds back no declaratie until `blocksBilling` is set there separately. Use `POST /signals/preview` to see what the rule matches first. 
         * @summary Create signal definition
         * @param {CreateSignalDefinitionRequest} createSignalDefinitionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createSignalDefinition(createSignalDefinitionRequest: CreateSignalDefinitionRequest, options?: RawAxiosRequestConfig): AxiosPromise<SignalDefinitionDTO> {
            return localVarFp.createSignalDefinition(createSignalDefinitionRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Delete a custom signal definition. System definitions (seeded by the platform) cannot be deleted. All associated signals will also be deleted. 
         * @summary Delete signal definition
         * @param {string} definitionId The ID of the signal definition to delete
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteSignalDefinition(definitionId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteSignalDefinition(definitionId, options).then((request) => request(axios, basePath));
        },
        /**
         * List signal definitions for the organization. This is an admin endpoint for managing signal definitions. 
         * @summary List signal definitions
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSignalDefinitions(options?: RawAxiosRequestConfig): AxiosPromise<Array<SignalDefinitionDTO>> {
            return localVarFp.listSignalDefinitions(options).then((request) => request(axios, basePath));
        },
        /**
         * Update a signal definition. Currently supports toggling the active/enabled status. 
         * @summary Update signal definition
         * @param {string} definitionId The ID of the signal definition to update
         * @param {UpdateSignalDefinitionRequest} updateSignalDefinitionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateSignalDefinition(definitionId: string, updateSignalDefinitionRequest: UpdateSignalDefinitionRequest, options?: RawAxiosRequestConfig): AxiosPromise<SignalDefinitionDTO> {
            return localVarFp.updateSignalDefinition(definitionId, updateSignalDefinitionRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SignalDefinitionsApi - object-oriented interface
 */
export class SignalDefinitionsApi extends BaseAPI {
    /**
     * Create a new custom signal definition for the organization. Custom definitions are organization-scoped and can be deleted. A new definition is created disabled and non-billing-blocking: it matches nothing until it is switched on with `PUT /signal-definitions/{id}`, and holds back no declaratie until `blocksBilling` is set there separately. Use `POST /signals/preview` to see what the rule matches first. 
     * @summary Create signal definition
     * @param {CreateSignalDefinitionRequest} createSignalDefinitionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createSignalDefinition(createSignalDefinitionRequest: CreateSignalDefinitionRequest, options?: RawAxiosRequestConfig) {
        return SignalDefinitionsApiFp(this.configuration).createSignalDefinition(createSignalDefinitionRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Delete a custom signal definition. System definitions (seeded by the platform) cannot be deleted. All associated signals will also be deleted. 
     * @summary Delete signal definition
     * @param {string} definitionId The ID of the signal definition to delete
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteSignalDefinition(definitionId: string, options?: RawAxiosRequestConfig) {
        return SignalDefinitionsApiFp(this.configuration).deleteSignalDefinition(definitionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List signal definitions for the organization. This is an admin endpoint for managing signal definitions. 
     * @summary List signal definitions
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listSignalDefinitions(options?: RawAxiosRequestConfig) {
        return SignalDefinitionsApiFp(this.configuration).listSignalDefinitions(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update a signal definition. Currently supports toggling the active/enabled status. 
     * @summary Update signal definition
     * @param {string} definitionId The ID of the signal definition to update
     * @param {UpdateSignalDefinitionRequest} updateSignalDefinitionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateSignalDefinition(definitionId: string, updateSignalDefinitionRequest: UpdateSignalDefinitionRequest, options?: RawAxiosRequestConfig) {
        return SignalDefinitionsApiFp(this.configuration).updateSignalDefinition(definitionId, updateSignalDefinitionRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * SignalDraftsApi - axios parameter creator
 */
export const SignalDraftsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Record that the author threw the draft away without saving it (ADR-034 disposition `DISCARDED`). Updates the attempt log; persists no signal definition.  Requires the signal-configuration management permission (`signal_definitions.manage`) and is organization-scoped. 
         * @summary Discard an authoring attempt without saving
         * @param {string} attemptId The id of the authoring attempt to discard (from generateSignalDraft).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        discardSignalDraft: async (attemptId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'attemptId' is not null or undefined
            assertParamExists('discardSignalDraft', 'attemptId', attemptId)
            const localVarPath = `/api/v1/signal-drafts/{attemptId}/discard`
                .replace(`{${"attemptId"}}`, encodeURIComponent(String(attemptId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Turn a plain-language description of a desired signal into a *validated* candidate rule (ADR-034). The generator proposes a rule; a deterministic validator disposes — an invalid candidate is never returned. The outcome is one of: `DRAFTED` (a validated candidate rule plus the assumptions the model made), `INEXPRESSIBLE` (the request cannot be expressed with the current schema), or `FAILED` (no valid rule could be produced within the bounded repair budget).  Every attempt is logged — successful or not — so the backlog of capability customers ask for is captured. Generating a draft persists **no** signal definition; the returned `attemptId` is used to save or discard the attempt in a separate, explicit step.  Requires the signal-configuration management permission (`signal_definitions.manage`) and is organization-scoped. 
         * @summary Generate a signal draft from a natural-language description
         * @param {GenerateSignalDraftRequest} generateSignalDraftRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateSignalDraft: async (generateSignalDraftRequest: GenerateSignalDraftRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'generateSignalDraftRequest' is not null or undefined
            assertParamExists('generateSignalDraft', 'generateSignalDraftRequest', generateSignalDraftRequest)
            const localVarPath = `/api/v1/signal-drafts`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(generateSignalDraftRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Persist an authored draft as a new `SignalDefinition`. The **ADR-034 guardrail is enforced here and must not be weakened**: an LLM-authored definition is always created **inactive** and **non-billing-blocking**. It can never go live or block invoicing without a separate, explicit human activation step (`PUT /signal-definitions/{id}`).  The rule submitted is re-validated deterministically; a rule that fails validation is rejected with `422` and nothing is persisted. The attempt log identified by `attemptId` is updated with the disposition (`ACCEPTED` when the rule is unchanged from the generated one, `EDITED` when the author changed it) and the id of the created definition.  Requires the signal-configuration management permission (`signal_definitions.manage`) and is organization-scoped. 
         * @summary Save a draft as an inactive, non-blocking signal definition
         * @param {string} attemptId The id of the authoring attempt to save (from generateSignalDraft).
         * @param {SaveSignalDraftRequest} saveSignalDraftRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveSignalDraft: async (attemptId: string, saveSignalDraftRequest: SaveSignalDraftRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'attemptId' is not null or undefined
            assertParamExists('saveSignalDraft', 'attemptId', attemptId)
            // verify required parameter 'saveSignalDraftRequest' is not null or undefined
            assertParamExists('saveSignalDraft', 'saveSignalDraftRequest', saveSignalDraftRequest)
            const localVarPath = `/api/v1/signal-drafts/{attemptId}/save`
                .replace(`{${"attemptId"}}`, encodeURIComponent(String(attemptId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(saveSignalDraftRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SignalDraftsApi - functional programming interface
 */
export const SignalDraftsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SignalDraftsApiAxiosParamCreator(configuration)
    return {
        /**
         * Record that the author threw the draft away without saving it (ADR-034 disposition `DISCARDED`). Updates the attempt log; persists no signal definition.  Requires the signal-configuration management permission (`signal_definitions.manage`) and is organization-scoped. 
         * @summary Discard an authoring attempt without saving
         * @param {string} attemptId The id of the authoring attempt to discard (from generateSignalDraft).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async discardSignalDraft(attemptId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.discardSignalDraft(attemptId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalDraftsApi.discardSignalDraft']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Turn a plain-language description of a desired signal into a *validated* candidate rule (ADR-034). The generator proposes a rule; a deterministic validator disposes — an invalid candidate is never returned. The outcome is one of: `DRAFTED` (a validated candidate rule plus the assumptions the model made), `INEXPRESSIBLE` (the request cannot be expressed with the current schema), or `FAILED` (no valid rule could be produced within the bounded repair budget).  Every attempt is logged — successful or not — so the backlog of capability customers ask for is captured. Generating a draft persists **no** signal definition; the returned `attemptId` is used to save or discard the attempt in a separate, explicit step.  Requires the signal-configuration management permission (`signal_definitions.manage`) and is organization-scoped. 
         * @summary Generate a signal draft from a natural-language description
         * @param {GenerateSignalDraftRequest} generateSignalDraftRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async generateSignalDraft(generateSignalDraftRequest: GenerateSignalDraftRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignalDraftDTO>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.generateSignalDraft(generateSignalDraftRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalDraftsApi.generateSignalDraft']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Persist an authored draft as a new `SignalDefinition`. The **ADR-034 guardrail is enforced here and must not be weakened**: an LLM-authored definition is always created **inactive** and **non-billing-blocking**. It can never go live or block invoicing without a separate, explicit human activation step (`PUT /signal-definitions/{id}`).  The rule submitted is re-validated deterministically; a rule that fails validation is rejected with `422` and nothing is persisted. The attempt log identified by `attemptId` is updated with the disposition (`ACCEPTED` when the rule is unchanged from the generated one, `EDITED` when the author changed it) and the id of the created definition.  Requires the signal-configuration management permission (`signal_definitions.manage`) and is organization-scoped. 
         * @summary Save a draft as an inactive, non-blocking signal definition
         * @param {string} attemptId The id of the authoring attempt to save (from generateSignalDraft).
         * @param {SaveSignalDraftRequest} saveSignalDraftRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async saveSignalDraft(attemptId: string, saveSignalDraftRequest: SaveSignalDraftRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignalDefinitionDTO>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.saveSignalDraft(attemptId, saveSignalDraftRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalDraftsApi.saveSignalDraft']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SignalDraftsApi - factory interface
 */
export const SignalDraftsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SignalDraftsApiFp(configuration)
    return {
        /**
         * Record that the author threw the draft away without saving it (ADR-034 disposition `DISCARDED`). Updates the attempt log; persists no signal definition.  Requires the signal-configuration management permission (`signal_definitions.manage`) and is organization-scoped. 
         * @summary Discard an authoring attempt without saving
         * @param {string} attemptId The id of the authoring attempt to discard (from generateSignalDraft).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        discardSignalDraft(attemptId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.discardSignalDraft(attemptId, options).then((request) => request(axios, basePath));
        },
        /**
         * Turn a plain-language description of a desired signal into a *validated* candidate rule (ADR-034). The generator proposes a rule; a deterministic validator disposes — an invalid candidate is never returned. The outcome is one of: `DRAFTED` (a validated candidate rule plus the assumptions the model made), `INEXPRESSIBLE` (the request cannot be expressed with the current schema), or `FAILED` (no valid rule could be produced within the bounded repair budget).  Every attempt is logged — successful or not — so the backlog of capability customers ask for is captured. Generating a draft persists **no** signal definition; the returned `attemptId` is used to save or discard the attempt in a separate, explicit step.  Requires the signal-configuration management permission (`signal_definitions.manage`) and is organization-scoped. 
         * @summary Generate a signal draft from a natural-language description
         * @param {GenerateSignalDraftRequest} generateSignalDraftRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateSignalDraft(generateSignalDraftRequest: GenerateSignalDraftRequest, options?: RawAxiosRequestConfig): AxiosPromise<SignalDraftDTO> {
            return localVarFp.generateSignalDraft(generateSignalDraftRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Persist an authored draft as a new `SignalDefinition`. The **ADR-034 guardrail is enforced here and must not be weakened**: an LLM-authored definition is always created **inactive** and **non-billing-blocking**. It can never go live or block invoicing without a separate, explicit human activation step (`PUT /signal-definitions/{id}`).  The rule submitted is re-validated deterministically; a rule that fails validation is rejected with `422` and nothing is persisted. The attempt log identified by `attemptId` is updated with the disposition (`ACCEPTED` when the rule is unchanged from the generated one, `EDITED` when the author changed it) and the id of the created definition.  Requires the signal-configuration management permission (`signal_definitions.manage`) and is organization-scoped. 
         * @summary Save a draft as an inactive, non-blocking signal definition
         * @param {string} attemptId The id of the authoring attempt to save (from generateSignalDraft).
         * @param {SaveSignalDraftRequest} saveSignalDraftRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveSignalDraft(attemptId: string, saveSignalDraftRequest: SaveSignalDraftRequest, options?: RawAxiosRequestConfig): AxiosPromise<SignalDefinitionDTO> {
            return localVarFp.saveSignalDraft(attemptId, saveSignalDraftRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SignalDraftsApi - object-oriented interface
 */
export class SignalDraftsApi extends BaseAPI {
    /**
     * Record that the author threw the draft away without saving it (ADR-034 disposition `DISCARDED`). Updates the attempt log; persists no signal definition.  Requires the signal-configuration management permission (`signal_definitions.manage`) and is organization-scoped. 
     * @summary Discard an authoring attempt without saving
     * @param {string} attemptId The id of the authoring attempt to discard (from generateSignalDraft).
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public discardSignalDraft(attemptId: string, options?: RawAxiosRequestConfig) {
        return SignalDraftsApiFp(this.configuration).discardSignalDraft(attemptId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Turn a plain-language description of a desired signal into a *validated* candidate rule (ADR-034). The generator proposes a rule; a deterministic validator disposes — an invalid candidate is never returned. The outcome is one of: `DRAFTED` (a validated candidate rule plus the assumptions the model made), `INEXPRESSIBLE` (the request cannot be expressed with the current schema), or `FAILED` (no valid rule could be produced within the bounded repair budget).  Every attempt is logged — successful or not — so the backlog of capability customers ask for is captured. Generating a draft persists **no** signal definition; the returned `attemptId` is used to save or discard the attempt in a separate, explicit step.  Requires the signal-configuration management permission (`signal_definitions.manage`) and is organization-scoped. 
     * @summary Generate a signal draft from a natural-language description
     * @param {GenerateSignalDraftRequest} generateSignalDraftRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public generateSignalDraft(generateSignalDraftRequest: GenerateSignalDraftRequest, options?: RawAxiosRequestConfig) {
        return SignalDraftsApiFp(this.configuration).generateSignalDraft(generateSignalDraftRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Persist an authored draft as a new `SignalDefinition`. The **ADR-034 guardrail is enforced here and must not be weakened**: an LLM-authored definition is always created **inactive** and **non-billing-blocking**. It can never go live or block invoicing without a separate, explicit human activation step (`PUT /signal-definitions/{id}`).  The rule submitted is re-validated deterministically; a rule that fails validation is rejected with `422` and nothing is persisted. The attempt log identified by `attemptId` is updated with the disposition (`ACCEPTED` when the rule is unchanged from the generated one, `EDITED` when the author changed it) and the id of the created definition.  Requires the signal-configuration management permission (`signal_definitions.manage`) and is organization-scoped. 
     * @summary Save a draft as an inactive, non-blocking signal definition
     * @param {string} attemptId The id of the authoring attempt to save (from generateSignalDraft).
     * @param {SaveSignalDraftRequest} saveSignalDraftRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public saveSignalDraft(attemptId: string, saveSignalDraftRequest: SaveSignalDraftRequest, options?: RawAxiosRequestConfig) {
        return SignalDraftsApiFp(this.configuration).saveSignalDraft(attemptId, saveSignalDraftRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * SignalPreviewApi - axios parameter creator
 */
export const SignalPreviewApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Read-only preview of a candidate signal rule against the caller\'s organization data (ADR-034). Establishes what a rule *means* before it is ever saved: \"this would currently flag N entities, e.g. these few\".  The candidate is first validated deterministically (the same gate used on save). An invalid candidate is **not** evaluated — the response carries `valid: false` and the structured validation errors so the caller can repair the rule. A valid candidate is run read-only through the signal evaluator, returning the number of matching entities plus a bounded sample of them.  Persists nothing and creates no signals. Strictly organization-scoped: a rule can only ever match the caller\'s own organization\'s data. Requires a signal-configuration permission (`signal_definitions.read` or `signal_definitions.manage`) and is rate-limited per user. 
         * @summary Preview a candidate signal rule
         * @param {SignalPreviewRequest} signalPreviewRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewSignalRule: async (signalPreviewRequest: SignalPreviewRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'signalPreviewRequest' is not null or undefined
            assertParamExists('previewSignalRule', 'signalPreviewRequest', signalPreviewRequest)
            const localVarPath = `/api/v1/signals/preview`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(signalPreviewRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SignalPreviewApi - functional programming interface
 */
export const SignalPreviewApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SignalPreviewApiAxiosParamCreator(configuration)
    return {
        /**
         * Read-only preview of a candidate signal rule against the caller\'s organization data (ADR-034). Establishes what a rule *means* before it is ever saved: \"this would currently flag N entities, e.g. these few\".  The candidate is first validated deterministically (the same gate used on save). An invalid candidate is **not** evaluated — the response carries `valid: false` and the structured validation errors so the caller can repair the rule. A valid candidate is run read-only through the signal evaluator, returning the number of matching entities plus a bounded sample of them.  Persists nothing and creates no signals. Strictly organization-scoped: a rule can only ever match the caller\'s own organization\'s data. Requires a signal-configuration permission (`signal_definitions.read` or `signal_definitions.manage`) and is rate-limited per user. 
         * @summary Preview a candidate signal rule
         * @param {SignalPreviewRequest} signalPreviewRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewSignalRule(signalPreviewRequest: SignalPreviewRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignalPreviewResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewSignalRule(signalPreviewRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalPreviewApi.previewSignalRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SignalPreviewApi - factory interface
 */
export const SignalPreviewApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SignalPreviewApiFp(configuration)
    return {
        /**
         * Read-only preview of a candidate signal rule against the caller\'s organization data (ADR-034). Establishes what a rule *means* before it is ever saved: \"this would currently flag N entities, e.g. these few\".  The candidate is first validated deterministically (the same gate used on save). An invalid candidate is **not** evaluated — the response carries `valid: false` and the structured validation errors so the caller can repair the rule. A valid candidate is run read-only through the signal evaluator, returning the number of matching entities plus a bounded sample of them.  Persists nothing and creates no signals. Strictly organization-scoped: a rule can only ever match the caller\'s own organization\'s data. Requires a signal-configuration permission (`signal_definitions.read` or `signal_definitions.manage`) and is rate-limited per user. 
         * @summary Preview a candidate signal rule
         * @param {SignalPreviewRequest} signalPreviewRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewSignalRule(signalPreviewRequest: SignalPreviewRequest, options?: RawAxiosRequestConfig): AxiosPromise<SignalPreviewResponse> {
            return localVarFp.previewSignalRule(signalPreviewRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SignalPreviewApi - object-oriented interface
 */
export class SignalPreviewApi extends BaseAPI {
    /**
     * Read-only preview of a candidate signal rule against the caller\'s organization data (ADR-034). Establishes what a rule *means* before it is ever saved: \"this would currently flag N entities, e.g. these few\".  The candidate is first validated deterministically (the same gate used on save). An invalid candidate is **not** evaluated — the response carries `valid: false` and the structured validation errors so the caller can repair the rule. A valid candidate is run read-only through the signal evaluator, returning the number of matching entities plus a bounded sample of them.  Persists nothing and creates no signals. Strictly organization-scoped: a rule can only ever match the caller\'s own organization\'s data. Requires a signal-configuration permission (`signal_definitions.read` or `signal_definitions.manage`) and is rate-limited per user. 
     * @summary Preview a candidate signal rule
     * @param {SignalPreviewRequest} signalPreviewRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewSignalRule(signalPreviewRequest: SignalPreviewRequest, options?: RawAxiosRequestConfig) {
        return SignalPreviewApiFp(this.configuration).previewSignalRule(signalPreviewRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * SignalSchemaApi - axios parameter creator
 */
export const SignalSchemaApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Get the entity graph schema for condition building. This endpoint returns the available entity types, their fields, and relationships that can be used when defining signal conditions, each with a human-readable description of what it means in domain terms.  The schema is static and organization-agnostic. Requires a signal-configuration permission (`signal_definitions.read` or `signal_definitions.manage`). 
         * @summary Get entity graph schema
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSignalSchema: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/signals/schema`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SignalSchemaApi - functional programming interface
 */
export const SignalSchemaApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SignalSchemaApiAxiosParamCreator(configuration)
    return {
        /**
         * Get the entity graph schema for condition building. This endpoint returns the available entity types, their fields, and relationships that can be used when defining signal conditions, each with a human-readable description of what it means in domain terms.  The schema is static and organization-agnostic. Requires a signal-configuration permission (`signal_definitions.read` or `signal_definitions.manage`). 
         * @summary Get entity graph schema
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getSignalSchema(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<EntityGraphSchema>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getSignalSchema(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalSchemaApi.getSignalSchema']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SignalSchemaApi - factory interface
 */
export const SignalSchemaApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SignalSchemaApiFp(configuration)
    return {
        /**
         * Get the entity graph schema for condition building. This endpoint returns the available entity types, their fields, and relationships that can be used when defining signal conditions, each with a human-readable description of what it means in domain terms.  The schema is static and organization-agnostic. Requires a signal-configuration permission (`signal_definitions.read` or `signal_definitions.manage`). 
         * @summary Get entity graph schema
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSignalSchema(options?: RawAxiosRequestConfig): AxiosPromise<EntityGraphSchema> {
            return localVarFp.getSignalSchema(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SignalSchemaApi - object-oriented interface
 */
export class SignalSchemaApi extends BaseAPI {
    /**
     * Get the entity graph schema for condition building. This endpoint returns the available entity types, their fields, and relationships that can be used when defining signal conditions, each with a human-readable description of what it means in domain terms.  The schema is static and organization-agnostic. Requires a signal-configuration permission (`signal_definitions.read` or `signal_definitions.manage`). 
     * @summary Get entity graph schema
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getSignalSchema(options?: RawAxiosRequestConfig) {
        return SignalSchemaApiFp(this.configuration).getSignalSchema(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * SignalSuppressionsApi - axios parameter creator
 */
export const SignalSuppressionsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Take a suppression back, so the signal it covered can be raised again.  The signal is **not** re-created by this call: the definition is evaluated again by the 15-minute safety-net sweep (ADR-015), which is how everything else in this domain resolves — a released billing line is likewise signalled again within the sweep rather than on release. If its condition has since been fixed, nothing comes back. 
         * @summary Lift a suppression
         * @param {string} suppressionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        liftSignalSuppression: async (suppressionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'suppressionId' is not null or undefined
            assertParamExists('liftSignalSuppression', 'suppressionId', suppressionId)
            const localVarPath = `/api/v1/signals/suppressions/{suppressionId}`
                .replace(`{${"suppressionId"}}`, encodeURIComponent(String(suppressionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The organization\'s **live** suppressions, newest judgement first, with who recorded each one, why, and when it runs out.  A suppression is org-wide by design — see `POST /api/v1/signals/{signalId}/suppress` — so this list is org-wide too, and `totalElements` is the \"n onderdrukt\" shown beside the signal count. Suppression stays countable, listable and reversible on purpose: a signal that vanished without trace would be a way to lose work.  An **expired** suppression is not listed. It is no longer suppressing anything and its signal is back on the board, so it is history rather than state; it is replaced if the same signal is put aside again. 
         * @summary List the signals the organization has put aside
         * @param {number} [page] Page number (0-based)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSignalSuppressions: async (page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/signals/suppressions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Stop showing one signal, without turning its definition off for the whole organization (`PUT /api/v1/signal-definitions/{definitionId}` with `enabled: false`, which is unchanged and remains the blunt instrument).  **The suppression is org-wide, and the actor is recorded.** These are shared clinical and billing prompts rather than personal notifications: a group session\'s signal reaches every participant\'s behandelaar, so a per-user dismissal would leave colleagues looking at a prompt one of them has already judged unactionable. What makes an org-wide dismissal safe is that it is recorded with who and why, listed at `GET /api/v1/signals/suppressions`, and reversible.  A suppression hangs on the signal\'s *identity* — its definition and root entity — not on its row id, so it survives the 15-minute re-derivation that would otherwise recreate the signal. The signal disappears from every surface immediately, including the billing blockers.  **A definition that blocks billing can only be snoozed.** `expiresAt` is then required and may be at most 90 days out: permanently suppressing a billing blocker hides money, and hides it in the way that is found at year-end. A non-blocking clinical prompt may be dismissed for good by omitting `expiresAt`. An expired suppression is not lifted — the signal simply matches again on the next sweep — and putting the same signal aside again replaces the earlier judgement. 
         * @summary Put one signal aside
         * @param {string} signalId 
         * @param {SuppressSignalRequest} suppressSignalRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suppressSignal: async (signalId: string, suppressSignalRequest: SuppressSignalRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'signalId' is not null or undefined
            assertParamExists('suppressSignal', 'signalId', signalId)
            // verify required parameter 'suppressSignalRequest' is not null or undefined
            assertParamExists('suppressSignal', 'suppressSignalRequest', suppressSignalRequest)
            const localVarPath = `/api/v1/signals/{signalId}/suppress`
                .replace(`{${"signalId"}}`, encodeURIComponent(String(signalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(suppressSignalRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SignalSuppressionsApi - functional programming interface
 */
export const SignalSuppressionsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SignalSuppressionsApiAxiosParamCreator(configuration)
    return {
        /**
         * Take a suppression back, so the signal it covered can be raised again.  The signal is **not** re-created by this call: the definition is evaluated again by the 15-minute safety-net sweep (ADR-015), which is how everything else in this domain resolves — a released billing line is likewise signalled again within the sweep rather than on release. If its condition has since been fixed, nothing comes back. 
         * @summary Lift a suppression
         * @param {string} suppressionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async liftSignalSuppression(suppressionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.liftSignalSuppression(suppressionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalSuppressionsApi.liftSignalSuppression']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The organization\'s **live** suppressions, newest judgement first, with who recorded each one, why, and when it runs out.  A suppression is org-wide by design — see `POST /api/v1/signals/{signalId}/suppress` — so this list is org-wide too, and `totalElements` is the \"n onderdrukt\" shown beside the signal count. Suppression stays countable, listable and reversible on purpose: a signal that vanished without trace would be a way to lose work.  An **expired** suppression is not listed. It is no longer suppressing anything and its signal is back on the board, so it is history rather than state; it is replaced if the same signal is put aside again. 
         * @summary List the signals the organization has put aside
         * @param {number} [page] Page number (0-based)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listSignalSuppressions(page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignalSuppressionPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listSignalSuppressions(page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalSuppressionsApi.listSignalSuppressions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Stop showing one signal, without turning its definition off for the whole organization (`PUT /api/v1/signal-definitions/{definitionId}` with `enabled: false`, which is unchanged and remains the blunt instrument).  **The suppression is org-wide, and the actor is recorded.** These are shared clinical and billing prompts rather than personal notifications: a group session\'s signal reaches every participant\'s behandelaar, so a per-user dismissal would leave colleagues looking at a prompt one of them has already judged unactionable. What makes an org-wide dismissal safe is that it is recorded with who and why, listed at `GET /api/v1/signals/suppressions`, and reversible.  A suppression hangs on the signal\'s *identity* — its definition and root entity — not on its row id, so it survives the 15-minute re-derivation that would otherwise recreate the signal. The signal disappears from every surface immediately, including the billing blockers.  **A definition that blocks billing can only be snoozed.** `expiresAt` is then required and may be at most 90 days out: permanently suppressing a billing blocker hides money, and hides it in the way that is found at year-end. A non-blocking clinical prompt may be dismissed for good by omitting `expiresAt`. An expired suppression is not lifted — the signal simply matches again on the next sweep — and putting the same signal aside again replaces the earlier judgement. 
         * @summary Put one signal aside
         * @param {string} signalId 
         * @param {SuppressSignalRequest} suppressSignalRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suppressSignal(signalId: string, suppressSignalRequest: SuppressSignalRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignalSuppressionDTO>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.suppressSignal(signalId, suppressSignalRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalSuppressionsApi.suppressSignal']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SignalSuppressionsApi - factory interface
 */
export const SignalSuppressionsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SignalSuppressionsApiFp(configuration)
    return {
        /**
         * Take a suppression back, so the signal it covered can be raised again.  The signal is **not** re-created by this call: the definition is evaluated again by the 15-minute safety-net sweep (ADR-015), which is how everything else in this domain resolves — a released billing line is likewise signalled again within the sweep rather than on release. If its condition has since been fixed, nothing comes back. 
         * @summary Lift a suppression
         * @param {string} suppressionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        liftSignalSuppression(suppressionId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.liftSignalSuppression(suppressionId, options).then((request) => request(axios, basePath));
        },
        /**
         * The organization\'s **live** suppressions, newest judgement first, with who recorded each one, why, and when it runs out.  A suppression is org-wide by design — see `POST /api/v1/signals/{signalId}/suppress` — so this list is org-wide too, and `totalElements` is the \"n onderdrukt\" shown beside the signal count. Suppression stays countable, listable and reversible on purpose: a signal that vanished without trace would be a way to lose work.  An **expired** suppression is not listed. It is no longer suppressing anything and its signal is back on the board, so it is history rather than state; it is replaced if the same signal is put aside again. 
         * @summary List the signals the organization has put aside
         * @param {number} [page] Page number (0-based)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSignalSuppressions(page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<SignalSuppressionPage> {
            return localVarFp.listSignalSuppressions(page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * Stop showing one signal, without turning its definition off for the whole organization (`PUT /api/v1/signal-definitions/{definitionId}` with `enabled: false`, which is unchanged and remains the blunt instrument).  **The suppression is org-wide, and the actor is recorded.** These are shared clinical and billing prompts rather than personal notifications: a group session\'s signal reaches every participant\'s behandelaar, so a per-user dismissal would leave colleagues looking at a prompt one of them has already judged unactionable. What makes an org-wide dismissal safe is that it is recorded with who and why, listed at `GET /api/v1/signals/suppressions`, and reversible.  A suppression hangs on the signal\'s *identity* — its definition and root entity — not on its row id, so it survives the 15-minute re-derivation that would otherwise recreate the signal. The signal disappears from every surface immediately, including the billing blockers.  **A definition that blocks billing can only be snoozed.** `expiresAt` is then required and may be at most 90 days out: permanently suppressing a billing blocker hides money, and hides it in the way that is found at year-end. A non-blocking clinical prompt may be dismissed for good by omitting `expiresAt`. An expired suppression is not lifted — the signal simply matches again on the next sweep — and putting the same signal aside again replaces the earlier judgement. 
         * @summary Put one signal aside
         * @param {string} signalId 
         * @param {SuppressSignalRequest} suppressSignalRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suppressSignal(signalId: string, suppressSignalRequest: SuppressSignalRequest, options?: RawAxiosRequestConfig): AxiosPromise<SignalSuppressionDTO> {
            return localVarFp.suppressSignal(signalId, suppressSignalRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SignalSuppressionsApi - object-oriented interface
 */
export class SignalSuppressionsApi extends BaseAPI {
    /**
     * Take a suppression back, so the signal it covered can be raised again.  The signal is **not** re-created by this call: the definition is evaluated again by the 15-minute safety-net sweep (ADR-015), which is how everything else in this domain resolves — a released billing line is likewise signalled again within the sweep rather than on release. If its condition has since been fixed, nothing comes back. 
     * @summary Lift a suppression
     * @param {string} suppressionId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public liftSignalSuppression(suppressionId: string, options?: RawAxiosRequestConfig) {
        return SignalSuppressionsApiFp(this.configuration).liftSignalSuppression(suppressionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The organization\'s **live** suppressions, newest judgement first, with who recorded each one, why, and when it runs out.  A suppression is org-wide by design — see `POST /api/v1/signals/{signalId}/suppress` — so this list is org-wide too, and `totalElements` is the \"n onderdrukt\" shown beside the signal count. Suppression stays countable, listable and reversible on purpose: a signal that vanished without trace would be a way to lose work.  An **expired** suppression is not listed. It is no longer suppressing anything and its signal is back on the board, so it is history rather than state; it is replaced if the same signal is put aside again. 
     * @summary List the signals the organization has put aside
     * @param {number} [page] Page number (0-based)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listSignalSuppressions(page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return SignalSuppressionsApiFp(this.configuration).listSignalSuppressions(page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Stop showing one signal, without turning its definition off for the whole organization (`PUT /api/v1/signal-definitions/{definitionId}` with `enabled: false`, which is unchanged and remains the blunt instrument).  **The suppression is org-wide, and the actor is recorded.** These are shared clinical and billing prompts rather than personal notifications: a group session\'s signal reaches every participant\'s behandelaar, so a per-user dismissal would leave colleagues looking at a prompt one of them has already judged unactionable. What makes an org-wide dismissal safe is that it is recorded with who and why, listed at `GET /api/v1/signals/suppressions`, and reversible.  A suppression hangs on the signal\'s *identity* — its definition and root entity — not on its row id, so it survives the 15-minute re-derivation that would otherwise recreate the signal. The signal disappears from every surface immediately, including the billing blockers.  **A definition that blocks billing can only be snoozed.** `expiresAt` is then required and may be at most 90 days out: permanently suppressing a billing blocker hides money, and hides it in the way that is found at year-end. A non-blocking clinical prompt may be dismissed for good by omitting `expiresAt`. An expired suppression is not lifted — the signal simply matches again on the next sweep — and putting the same signal aside again replaces the earlier judgement. 
     * @summary Put one signal aside
     * @param {string} signalId 
     * @param {SuppressSignalRequest} suppressSignalRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public suppressSignal(signalId: string, suppressSignalRequest: SuppressSignalRequest, options?: RawAxiosRequestConfig) {
        return SignalSuppressionsApiFp(this.configuration).suppressSignal(signalId, suppressSignalRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * SignalsApi - axios parameter creator
 */
export const SignalsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns every signal bucket the dashboard renders in a single response, in one round-trip. This removes the synchronized request burst (one request per bucket) that previously saturated the backend on dashboard load — it is the sole entry point for the dashboard\'s signal buckets.  Each bucket (`myClients`, `personalPractitioner`, `billingAsPractitioner`, `organizationLevel`, `visibleToMe`) keeps its own pagination (page/size). The `organizationLevel` bucket is admin-gated: it is present only for users with `organization_settings.update`; non-admins receive a response without it (no 403). 
         * @summary Get all dashboard signal buckets in one request
         * @param {number} [myClientsPage] Page number (0-based) for the my-clients bucket
         * @param {number} [myClientsSize] Page size for the my-clients bucket
         * @param {number} [personalPractitionerPage] Page number (0-based) for the personal-practitioner bucket
         * @param {number} [personalPractitionerSize] Page size for the personal-practitioner bucket
         * @param {number} [billingAsPractitionerPage] Page number (0-based) for the billing-as-practitioner bucket
         * @param {number} [billingAsPractitionerSize] Page size for the billing-as-practitioner bucket
         * @param {number} [organizationLevelPage] Page number (0-based) for the organization-level bucket (admins only)
         * @param {number} [organizationLevelSize] Page size for the organization-level bucket (admins only)
         * @param {number} [visibleToMePage] Page number (0-based) for the visible-to-me bucket
         * @param {number} [visibleToMeSize] Page size for the visible-to-me bucket
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getDashboardSignals: async (myClientsPage?: number, myClientsSize?: number, personalPractitionerPage?: number, personalPractitionerSize?: number, billingAsPractitionerPage?: number, billingAsPractitionerSize?: number, organizationLevelPage?: number, organizationLevelSize?: number, visibleToMePage?: number, visibleToMeSize?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/signals/dashboard`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (myClientsPage !== undefined) {
                localVarQueryParameter['myClientsPage'] = myClientsPage;
            }

            if (myClientsSize !== undefined) {
                localVarQueryParameter['myClientsSize'] = myClientsSize;
            }

            if (personalPractitionerPage !== undefined) {
                localVarQueryParameter['personalPractitionerPage'] = personalPractitionerPage;
            }

            if (personalPractitionerSize !== undefined) {
                localVarQueryParameter['personalPractitionerSize'] = personalPractitionerSize;
            }

            if (billingAsPractitionerPage !== undefined) {
                localVarQueryParameter['billingAsPractitionerPage'] = billingAsPractitionerPage;
            }

            if (billingAsPractitionerSize !== undefined) {
                localVarQueryParameter['billingAsPractitionerSize'] = billingAsPractitionerSize;
            }

            if (organizationLevelPage !== undefined) {
                localVarQueryParameter['organizationLevelPage'] = organizationLevelPage;
            }

            if (organizationLevelSize !== undefined) {
                localVarQueryParameter['organizationLevelSize'] = organizationLevelSize;
            }

            if (visibleToMePage !== undefined) {
                localVarQueryParameter['visibleToMePage'] = visibleToMePage;
            }

            if (visibleToMeSize !== undefined) {
                localVarQueryParameter['visibleToMeSize'] = visibleToMeSize;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List signals whose entity chain resolves to the given client. Includes signals rooted directly on the client as well as signals on the client\'s appointments, care trajectories, documents, form submissions, and appointment facts.  Returns paginated results sorted by creation time (newest first). 
         * @summary List signals for a client
         * @param {string} clientId The ID of the client
         * @param {number} [page] Page number (0-based)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientSignals: async (clientId: string, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('listClientSignals', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/signals`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List signals for the current user\'s organization, filtered by root entity type and optionally by root entity ID. Used by surfaces that scope to a single entity (e.g. the appointment detail panel).  Audience-derived listings (dashboard, banner, client dossier, claims) go through the dedicated `/visible-to-me`, `/clients/{id}/signals`, and `/billing/blocking-signals` endpoints — there is no longer a routing-tag query parameter.  Returns paginated results sorted by creation time (newest first). 
         * @summary List signals by root entity
         * @param {SignalRootEntity} rootEntityType Filter by root entity type
         * @param {string} [rootEntityId] Optional filter by root entity ID (e.g., appointment ID)
         * @param {number} [page] Page number (0-based)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSignals: async (rootEntityType: SignalRootEntity, rootEntityId?: string, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'rootEntityType' is not null or undefined
            assertParamExists('listSignals', 'rootEntityType', rootEntityType)
            const localVarPath = `/api/v1/signals`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (rootEntityType !== undefined) {
                localVarQueryParameter['rootEntityType'] = rootEntityType;
            }

            if (rootEntityId !== undefined) {
                localVarQueryParameter['rootEntityId'] = rootEntityId;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List signals where the current user is in the audience for the entity: - Behandelaar of the client (CLIENT, CARE_TRAJECTORY, APPOINTMENT, APPOINTMENT_FACT chains) - Directly the practitioner (PRACTITIONER root) - Org admin (sees all org signals)  Replaces the surface filtering that was previously expressed via the `context` query parameter on `/api/v1/signals`. Returns paginated results sorted by creation time (newest first).  The filter parameters narrow that audience set; none of them can widen it. A signal the audience rules exclude stays excluded whatever is filtered on — the filters are applied on top of the audience predicate, never instead of it. 
         * @summary List signals visible to the current user
         * @param {number} [page] Page number (0-based)
         * @param {number} [size] Page size
         * @param {string} [clientId] Only signals whose entity chain resolves to this client — rooted on the client itself or on its appointments, care trajectories, documents, form submissions and appointment facts. Signals rooted on organization-level entities (ORGANIZATION/LOCATION/PRACTITIONER/INSURER/CONTRACT) are not client-specific and never match. 
         * @param {Array<string>} [definitionKey] Only signals raised by one of these signal definitions. Repeatable; values combine as OR.
         * @param {Array<SignalSeverity>} [severity] Only signals of one of these severities. Repeatable; values combine as OR.
         * @param {Array<SignalRootEntity>} [rootEntityType] Only signals rooted on one of these entity types. Repeatable; values combine as OR.
         * @param {boolean} [blocksBilling] &#x60;true&#x60; for only the signals that hold back an invoice and a GDS801 export, &#x60;false&#x60; for only those that do not. Omit for both.  This is a different axis from &#x60;severity&#x60;, not a reading of it: severity says how loud a signal is, &#x60;blocksBilling&#x60; says whether it stops a declaration. Both are set independently per signal definition. 
         * @param {string} [search] Free-text match, case-insensitive, on the client the signal is about and on the signal\&#39;s own name and description.  The *rendered* message is deliberately not matched: it does not exist in the database — it is produced per request from the definition\&#39;s template and the signal\&#39;s cached context — so a server-side query cannot see it. What a user types is the client\&#39;s name or the wording of the signal type, and both of those are matched. 
         * @param {string} [sort] Sort order as &#x60;field,direction&#x60;. Supported fields: createdAt. Any other field falls back to createdAt. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSignalsVisibleToMe: async (page?: number, size?: number, clientId?: string, definitionKey?: Array<string>, severity?: Array<SignalSeverity>, rootEntityType?: Array<SignalRootEntity>, blocksBilling?: boolean, search?: string, sort?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/signals/visible-to-me`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (definitionKey) {
                localVarQueryParameter['definitionKey'] = definitionKey;
            }

            if (severity) {
                localVarQueryParameter['severity'] = severity;
            }

            if (rootEntityType) {
                localVarQueryParameter['rootEntityType'] = rootEntityType;
            }

            if (blocksBilling !== undefined) {
                localVarQueryParameter['blocksBilling'] = blocksBilling;
            }

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The same audience set as `/api/v1/signals/visible-to-me` and the same filters, counted per signal definition instead of listed per occurrence.  A small number of causes multiplied across many rows is what a signal backlog actually is — one missing AGB-code across twelve behandelaars is hundreds of signals. Per occurrence that is noise; per definition it is a short worklist (#3830).  Each count is computed over the **whole** filtered set, not over a page of it: a count taken from a loaded page would be wrong the moment the list is paged, which is the one thing this endpoint exists to avoid. Expanding a group means calling `/api/v1/signals/visible-to-me` with the same filters plus that group\'s `definitionKey`, so the rows under a group are the rows it counted. 
         * @summary Count the signals visible to the current user, per signal definition
         * @param {string} [clientId] Only signals whose entity chain resolves to this client — rooted on the client itself or on its appointments, care trajectories, documents, form submissions and appointment facts. Signals rooted on organization-level entities (ORGANIZATION/LOCATION/PRACTITIONER/INSURER/CONTRACT) are not client-specific and never match. 
         * @param {Array<string>} [definitionKey] Only signals raised by one of these signal definitions. Repeatable; values combine as OR.
         * @param {Array<SignalSeverity>} [severity] Only signals of one of these severities. Repeatable; values combine as OR.
         * @param {Array<SignalRootEntity>} [rootEntityType] Only signals rooted on one of these entity types. Repeatable; values combine as OR.
         * @param {boolean} [blocksBilling] &#x60;true&#x60; for only the signals that hold back an invoice and a GDS801 export, &#x60;false&#x60; for only those that do not. Omit for both.  This is a different axis from &#x60;severity&#x60;, not a reading of it: severity says how loud a signal is, &#x60;blocksBilling&#x60; says whether it stops a declaration. Both are set independently per signal definition. 
         * @param {string} [search] Free-text match, case-insensitive, on the client the signal is about and on the signal\&#39;s own name and description.  The *rendered* message is deliberately not matched: it does not exist in the database — it is produced per request from the definition\&#39;s template and the signal\&#39;s cached context — so a server-side query cannot see it. What a user types is the client\&#39;s name or the wording of the signal type, and both of those are matched. 
         * @param {string} [sort] Sort order as &#x60;field,direction&#x60;. Supported fields: count, name. Any other field falls back to count. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        summarizeSignalsVisibleToMe: async (clientId?: string, definitionKey?: Array<string>, severity?: Array<SignalSeverity>, rootEntityType?: Array<SignalRootEntity>, blocksBilling?: boolean, search?: string, sort?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/signals/visible-to-me/groups`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (definitionKey) {
                localVarQueryParameter['definitionKey'] = definitionKey;
            }

            if (severity) {
                localVarQueryParameter['severity'] = severity;
            }

            if (rootEntityType) {
                localVarQueryParameter['rootEntityType'] = rootEntityType;
            }

            if (blocksBilling !== undefined) {
                localVarQueryParameter['blocksBilling'] = blocksBilling;
            }

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SignalsApi - functional programming interface
 */
export const SignalsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SignalsApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns every signal bucket the dashboard renders in a single response, in one round-trip. This removes the synchronized request burst (one request per bucket) that previously saturated the backend on dashboard load — it is the sole entry point for the dashboard\'s signal buckets.  Each bucket (`myClients`, `personalPractitioner`, `billingAsPractitioner`, `organizationLevel`, `visibleToMe`) keeps its own pagination (page/size). The `organizationLevel` bucket is admin-gated: it is present only for users with `organization_settings.update`; non-admins receive a response without it (no 403). 
         * @summary Get all dashboard signal buckets in one request
         * @param {number} [myClientsPage] Page number (0-based) for the my-clients bucket
         * @param {number} [myClientsSize] Page size for the my-clients bucket
         * @param {number} [personalPractitionerPage] Page number (0-based) for the personal-practitioner bucket
         * @param {number} [personalPractitionerSize] Page size for the personal-practitioner bucket
         * @param {number} [billingAsPractitionerPage] Page number (0-based) for the billing-as-practitioner bucket
         * @param {number} [billingAsPractitionerSize] Page size for the billing-as-practitioner bucket
         * @param {number} [organizationLevelPage] Page number (0-based) for the organization-level bucket (admins only)
         * @param {number} [organizationLevelSize] Page size for the organization-level bucket (admins only)
         * @param {number} [visibleToMePage] Page number (0-based) for the visible-to-me bucket
         * @param {number} [visibleToMeSize] Page size for the visible-to-me bucket
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getDashboardSignals(myClientsPage?: number, myClientsSize?: number, personalPractitionerPage?: number, personalPractitionerSize?: number, billingAsPractitionerPage?: number, billingAsPractitionerSize?: number, organizationLevelPage?: number, organizationLevelSize?: number, visibleToMePage?: number, visibleToMeSize?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DashboardSignalsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getDashboardSignals(myClientsPage, myClientsSize, personalPractitionerPage, personalPractitionerSize, billingAsPractitionerPage, billingAsPractitionerSize, organizationLevelPage, organizationLevelSize, visibleToMePage, visibleToMeSize, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalsApi.getDashboardSignals']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List signals whose entity chain resolves to the given client. Includes signals rooted directly on the client as well as signals on the client\'s appointments, care trajectories, documents, form submissions, and appointment facts.  Returns paginated results sorted by creation time (newest first). 
         * @summary List signals for a client
         * @param {string} clientId The ID of the client
         * @param {number} [page] Page number (0-based)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClientSignals(clientId: string, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignalPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClientSignals(clientId, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalsApi.listClientSignals']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List signals for the current user\'s organization, filtered by root entity type and optionally by root entity ID. Used by surfaces that scope to a single entity (e.g. the appointment detail panel).  Audience-derived listings (dashboard, banner, client dossier, claims) go through the dedicated `/visible-to-me`, `/clients/{id}/signals`, and `/billing/blocking-signals` endpoints — there is no longer a routing-tag query parameter.  Returns paginated results sorted by creation time (newest first). 
         * @summary List signals by root entity
         * @param {SignalRootEntity} rootEntityType Filter by root entity type
         * @param {string} [rootEntityId] Optional filter by root entity ID (e.g., appointment ID)
         * @param {number} [page] Page number (0-based)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listSignals(rootEntityType: SignalRootEntity, rootEntityId?: string, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignalPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listSignals(rootEntityType, rootEntityId, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalsApi.listSignals']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List signals where the current user is in the audience for the entity: - Behandelaar of the client (CLIENT, CARE_TRAJECTORY, APPOINTMENT, APPOINTMENT_FACT chains) - Directly the practitioner (PRACTITIONER root) - Org admin (sees all org signals)  Replaces the surface filtering that was previously expressed via the `context` query parameter on `/api/v1/signals`. Returns paginated results sorted by creation time (newest first).  The filter parameters narrow that audience set; none of them can widen it. A signal the audience rules exclude stays excluded whatever is filtered on — the filters are applied on top of the audience predicate, never instead of it. 
         * @summary List signals visible to the current user
         * @param {number} [page] Page number (0-based)
         * @param {number} [size] Page size
         * @param {string} [clientId] Only signals whose entity chain resolves to this client — rooted on the client itself or on its appointments, care trajectories, documents, form submissions and appointment facts. Signals rooted on organization-level entities (ORGANIZATION/LOCATION/PRACTITIONER/INSURER/CONTRACT) are not client-specific and never match. 
         * @param {Array<string>} [definitionKey] Only signals raised by one of these signal definitions. Repeatable; values combine as OR.
         * @param {Array<SignalSeverity>} [severity] Only signals of one of these severities. Repeatable; values combine as OR.
         * @param {Array<SignalRootEntity>} [rootEntityType] Only signals rooted on one of these entity types. Repeatable; values combine as OR.
         * @param {boolean} [blocksBilling] &#x60;true&#x60; for only the signals that hold back an invoice and a GDS801 export, &#x60;false&#x60; for only those that do not. Omit for both.  This is a different axis from &#x60;severity&#x60;, not a reading of it: severity says how loud a signal is, &#x60;blocksBilling&#x60; says whether it stops a declaration. Both are set independently per signal definition. 
         * @param {string} [search] Free-text match, case-insensitive, on the client the signal is about and on the signal\&#39;s own name and description.  The *rendered* message is deliberately not matched: it does not exist in the database — it is produced per request from the definition\&#39;s template and the signal\&#39;s cached context — so a server-side query cannot see it. What a user types is the client\&#39;s name or the wording of the signal type, and both of those are matched. 
         * @param {string} [sort] Sort order as &#x60;field,direction&#x60;. Supported fields: createdAt. Any other field falls back to createdAt. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listSignalsVisibleToMe(page?: number, size?: number, clientId?: string, definitionKey?: Array<string>, severity?: Array<SignalSeverity>, rootEntityType?: Array<SignalRootEntity>, blocksBilling?: boolean, search?: string, sort?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignalPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listSignalsVisibleToMe(page, size, clientId, definitionKey, severity, rootEntityType, blocksBilling, search, sort, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalsApi.listSignalsVisibleToMe']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The same audience set as `/api/v1/signals/visible-to-me` and the same filters, counted per signal definition instead of listed per occurrence.  A small number of causes multiplied across many rows is what a signal backlog actually is — one missing AGB-code across twelve behandelaars is hundreds of signals. Per occurrence that is noise; per definition it is a short worklist (#3830).  Each count is computed over the **whole** filtered set, not over a page of it: a count taken from a loaded page would be wrong the moment the list is paged, which is the one thing this endpoint exists to avoid. Expanding a group means calling `/api/v1/signals/visible-to-me` with the same filters plus that group\'s `definitionKey`, so the rows under a group are the rows it counted. 
         * @summary Count the signals visible to the current user, per signal definition
         * @param {string} [clientId] Only signals whose entity chain resolves to this client — rooted on the client itself or on its appointments, care trajectories, documents, form submissions and appointment facts. Signals rooted on organization-level entities (ORGANIZATION/LOCATION/PRACTITIONER/INSURER/CONTRACT) are not client-specific and never match. 
         * @param {Array<string>} [definitionKey] Only signals raised by one of these signal definitions. Repeatable; values combine as OR.
         * @param {Array<SignalSeverity>} [severity] Only signals of one of these severities. Repeatable; values combine as OR.
         * @param {Array<SignalRootEntity>} [rootEntityType] Only signals rooted on one of these entity types. Repeatable; values combine as OR.
         * @param {boolean} [blocksBilling] &#x60;true&#x60; for only the signals that hold back an invoice and a GDS801 export, &#x60;false&#x60; for only those that do not. Omit for both.  This is a different axis from &#x60;severity&#x60;, not a reading of it: severity says how loud a signal is, &#x60;blocksBilling&#x60; says whether it stops a declaration. Both are set independently per signal definition. 
         * @param {string} [search] Free-text match, case-insensitive, on the client the signal is about and on the signal\&#39;s own name and description.  The *rendered* message is deliberately not matched: it does not exist in the database — it is produced per request from the definition\&#39;s template and the signal\&#39;s cached context — so a server-side query cannot see it. What a user types is the client\&#39;s name or the wording of the signal type, and both of those are matched. 
         * @param {string} [sort] Sort order as &#x60;field,direction&#x60;. Supported fields: count, name. Any other field falls back to count. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async summarizeSignalsVisibleToMe(clientId?: string, definitionKey?: Array<string>, severity?: Array<SignalSeverity>, rootEntityType?: Array<SignalRootEntity>, blocksBilling?: boolean, search?: string, sort?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignalGroupSummary>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.summarizeSignalsVisibleToMe(clientId, definitionKey, severity, rootEntityType, blocksBilling, search, sort, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignalsApi.summarizeSignalsVisibleToMe']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SignalsApi - factory interface
 */
export const SignalsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SignalsApiFp(configuration)
    return {
        /**
         * Returns every signal bucket the dashboard renders in a single response, in one round-trip. This removes the synchronized request burst (one request per bucket) that previously saturated the backend on dashboard load — it is the sole entry point for the dashboard\'s signal buckets.  Each bucket (`myClients`, `personalPractitioner`, `billingAsPractitioner`, `organizationLevel`, `visibleToMe`) keeps its own pagination (page/size). The `organizationLevel` bucket is admin-gated: it is present only for users with `organization_settings.update`; non-admins receive a response without it (no 403). 
         * @summary Get all dashboard signal buckets in one request
         * @param {number} [myClientsPage] Page number (0-based) for the my-clients bucket
         * @param {number} [myClientsSize] Page size for the my-clients bucket
         * @param {number} [personalPractitionerPage] Page number (0-based) for the personal-practitioner bucket
         * @param {number} [personalPractitionerSize] Page size for the personal-practitioner bucket
         * @param {number} [billingAsPractitionerPage] Page number (0-based) for the billing-as-practitioner bucket
         * @param {number} [billingAsPractitionerSize] Page size for the billing-as-practitioner bucket
         * @param {number} [organizationLevelPage] Page number (0-based) for the organization-level bucket (admins only)
         * @param {number} [organizationLevelSize] Page size for the organization-level bucket (admins only)
         * @param {number} [visibleToMePage] Page number (0-based) for the visible-to-me bucket
         * @param {number} [visibleToMeSize] Page size for the visible-to-me bucket
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getDashboardSignals(myClientsPage?: number, myClientsSize?: number, personalPractitionerPage?: number, personalPractitionerSize?: number, billingAsPractitionerPage?: number, billingAsPractitionerSize?: number, organizationLevelPage?: number, organizationLevelSize?: number, visibleToMePage?: number, visibleToMeSize?: number, options?: RawAxiosRequestConfig): AxiosPromise<DashboardSignalsResponse> {
            return localVarFp.getDashboardSignals(myClientsPage, myClientsSize, personalPractitionerPage, personalPractitionerSize, billingAsPractitionerPage, billingAsPractitionerSize, organizationLevelPage, organizationLevelSize, visibleToMePage, visibleToMeSize, options).then((request) => request(axios, basePath));
        },
        /**
         * List signals whose entity chain resolves to the given client. Includes signals rooted directly on the client as well as signals on the client\'s appointments, care trajectories, documents, form submissions, and appointment facts.  Returns paginated results sorted by creation time (newest first). 
         * @summary List signals for a client
         * @param {string} clientId The ID of the client
         * @param {number} [page] Page number (0-based)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientSignals(clientId: string, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<SignalPage> {
            return localVarFp.listClientSignals(clientId, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * List signals for the current user\'s organization, filtered by root entity type and optionally by root entity ID. Used by surfaces that scope to a single entity (e.g. the appointment detail panel).  Audience-derived listings (dashboard, banner, client dossier, claims) go through the dedicated `/visible-to-me`, `/clients/{id}/signals`, and `/billing/blocking-signals` endpoints — there is no longer a routing-tag query parameter.  Returns paginated results sorted by creation time (newest first). 
         * @summary List signals by root entity
         * @param {SignalRootEntity} rootEntityType Filter by root entity type
         * @param {string} [rootEntityId] Optional filter by root entity ID (e.g., appointment ID)
         * @param {number} [page] Page number (0-based)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSignals(rootEntityType: SignalRootEntity, rootEntityId?: string, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<SignalPage> {
            return localVarFp.listSignals(rootEntityType, rootEntityId, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * List signals where the current user is in the audience for the entity: - Behandelaar of the client (CLIENT, CARE_TRAJECTORY, APPOINTMENT, APPOINTMENT_FACT chains) - Directly the practitioner (PRACTITIONER root) - Org admin (sees all org signals)  Replaces the surface filtering that was previously expressed via the `context` query parameter on `/api/v1/signals`. Returns paginated results sorted by creation time (newest first).  The filter parameters narrow that audience set; none of them can widen it. A signal the audience rules exclude stays excluded whatever is filtered on — the filters are applied on top of the audience predicate, never instead of it. 
         * @summary List signals visible to the current user
         * @param {number} [page] Page number (0-based)
         * @param {number} [size] Page size
         * @param {string} [clientId] Only signals whose entity chain resolves to this client — rooted on the client itself or on its appointments, care trajectories, documents, form submissions and appointment facts. Signals rooted on organization-level entities (ORGANIZATION/LOCATION/PRACTITIONER/INSURER/CONTRACT) are not client-specific and never match. 
         * @param {Array<string>} [definitionKey] Only signals raised by one of these signal definitions. Repeatable; values combine as OR.
         * @param {Array<SignalSeverity>} [severity] Only signals of one of these severities. Repeatable; values combine as OR.
         * @param {Array<SignalRootEntity>} [rootEntityType] Only signals rooted on one of these entity types. Repeatable; values combine as OR.
         * @param {boolean} [blocksBilling] &#x60;true&#x60; for only the signals that hold back an invoice and a GDS801 export, &#x60;false&#x60; for only those that do not. Omit for both.  This is a different axis from &#x60;severity&#x60;, not a reading of it: severity says how loud a signal is, &#x60;blocksBilling&#x60; says whether it stops a declaration. Both are set independently per signal definition. 
         * @param {string} [search] Free-text match, case-insensitive, on the client the signal is about and on the signal\&#39;s own name and description.  The *rendered* message is deliberately not matched: it does not exist in the database — it is produced per request from the definition\&#39;s template and the signal\&#39;s cached context — so a server-side query cannot see it. What a user types is the client\&#39;s name or the wording of the signal type, and both of those are matched. 
         * @param {string} [sort] Sort order as &#x60;field,direction&#x60;. Supported fields: createdAt. Any other field falls back to createdAt. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSignalsVisibleToMe(page?: number, size?: number, clientId?: string, definitionKey?: Array<string>, severity?: Array<SignalSeverity>, rootEntityType?: Array<SignalRootEntity>, blocksBilling?: boolean, search?: string, sort?: string, options?: RawAxiosRequestConfig): AxiosPromise<SignalPage> {
            return localVarFp.listSignalsVisibleToMe(page, size, clientId, definitionKey, severity, rootEntityType, blocksBilling, search, sort, options).then((request) => request(axios, basePath));
        },
        /**
         * The same audience set as `/api/v1/signals/visible-to-me` and the same filters, counted per signal definition instead of listed per occurrence.  A small number of causes multiplied across many rows is what a signal backlog actually is — one missing AGB-code across twelve behandelaars is hundreds of signals. Per occurrence that is noise; per definition it is a short worklist (#3830).  Each count is computed over the **whole** filtered set, not over a page of it: a count taken from a loaded page would be wrong the moment the list is paged, which is the one thing this endpoint exists to avoid. Expanding a group means calling `/api/v1/signals/visible-to-me` with the same filters plus that group\'s `definitionKey`, so the rows under a group are the rows it counted. 
         * @summary Count the signals visible to the current user, per signal definition
         * @param {string} [clientId] Only signals whose entity chain resolves to this client — rooted on the client itself or on its appointments, care trajectories, documents, form submissions and appointment facts. Signals rooted on organization-level entities (ORGANIZATION/LOCATION/PRACTITIONER/INSURER/CONTRACT) are not client-specific and never match. 
         * @param {Array<string>} [definitionKey] Only signals raised by one of these signal definitions. Repeatable; values combine as OR.
         * @param {Array<SignalSeverity>} [severity] Only signals of one of these severities. Repeatable; values combine as OR.
         * @param {Array<SignalRootEntity>} [rootEntityType] Only signals rooted on one of these entity types. Repeatable; values combine as OR.
         * @param {boolean} [blocksBilling] &#x60;true&#x60; for only the signals that hold back an invoice and a GDS801 export, &#x60;false&#x60; for only those that do not. Omit for both.  This is a different axis from &#x60;severity&#x60;, not a reading of it: severity says how loud a signal is, &#x60;blocksBilling&#x60; says whether it stops a declaration. Both are set independently per signal definition. 
         * @param {string} [search] Free-text match, case-insensitive, on the client the signal is about and on the signal\&#39;s own name and description.  The *rendered* message is deliberately not matched: it does not exist in the database — it is produced per request from the definition\&#39;s template and the signal\&#39;s cached context — so a server-side query cannot see it. What a user types is the client\&#39;s name or the wording of the signal type, and both of those are matched. 
         * @param {string} [sort] Sort order as &#x60;field,direction&#x60;. Supported fields: count, name. Any other field falls back to count. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        summarizeSignalsVisibleToMe(clientId?: string, definitionKey?: Array<string>, severity?: Array<SignalSeverity>, rootEntityType?: Array<SignalRootEntity>, blocksBilling?: boolean, search?: string, sort?: string, options?: RawAxiosRequestConfig): AxiosPromise<SignalGroupSummary> {
            return localVarFp.summarizeSignalsVisibleToMe(clientId, definitionKey, severity, rootEntityType, blocksBilling, search, sort, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SignalsApi - object-oriented interface
 */
export class SignalsApi extends BaseAPI {
    /**
     * Returns every signal bucket the dashboard renders in a single response, in one round-trip. This removes the synchronized request burst (one request per bucket) that previously saturated the backend on dashboard load — it is the sole entry point for the dashboard\'s signal buckets.  Each bucket (`myClients`, `personalPractitioner`, `billingAsPractitioner`, `organizationLevel`, `visibleToMe`) keeps its own pagination (page/size). The `organizationLevel` bucket is admin-gated: it is present only for users with `organization_settings.update`; non-admins receive a response without it (no 403). 
     * @summary Get all dashboard signal buckets in one request
     * @param {number} [myClientsPage] Page number (0-based) for the my-clients bucket
     * @param {number} [myClientsSize] Page size for the my-clients bucket
     * @param {number} [personalPractitionerPage] Page number (0-based) for the personal-practitioner bucket
     * @param {number} [personalPractitionerSize] Page size for the personal-practitioner bucket
     * @param {number} [billingAsPractitionerPage] Page number (0-based) for the billing-as-practitioner bucket
     * @param {number} [billingAsPractitionerSize] Page size for the billing-as-practitioner bucket
     * @param {number} [organizationLevelPage] Page number (0-based) for the organization-level bucket (admins only)
     * @param {number} [organizationLevelSize] Page size for the organization-level bucket (admins only)
     * @param {number} [visibleToMePage] Page number (0-based) for the visible-to-me bucket
     * @param {number} [visibleToMeSize] Page size for the visible-to-me bucket
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getDashboardSignals(myClientsPage?: number, myClientsSize?: number, personalPractitionerPage?: number, personalPractitionerSize?: number, billingAsPractitionerPage?: number, billingAsPractitionerSize?: number, organizationLevelPage?: number, organizationLevelSize?: number, visibleToMePage?: number, visibleToMeSize?: number, options?: RawAxiosRequestConfig) {
        return SignalsApiFp(this.configuration).getDashboardSignals(myClientsPage, myClientsSize, personalPractitionerPage, personalPractitionerSize, billingAsPractitionerPage, billingAsPractitionerSize, organizationLevelPage, organizationLevelSize, visibleToMePage, visibleToMeSize, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List signals whose entity chain resolves to the given client. Includes signals rooted directly on the client as well as signals on the client\'s appointments, care trajectories, documents, form submissions, and appointment facts.  Returns paginated results sorted by creation time (newest first). 
     * @summary List signals for a client
     * @param {string} clientId The ID of the client
     * @param {number} [page] Page number (0-based)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClientSignals(clientId: string, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return SignalsApiFp(this.configuration).listClientSignals(clientId, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List signals for the current user\'s organization, filtered by root entity type and optionally by root entity ID. Used by surfaces that scope to a single entity (e.g. the appointment detail panel).  Audience-derived listings (dashboard, banner, client dossier, claims) go through the dedicated `/visible-to-me`, `/clients/{id}/signals`, and `/billing/blocking-signals` endpoints — there is no longer a routing-tag query parameter.  Returns paginated results sorted by creation time (newest first). 
     * @summary List signals by root entity
     * @param {SignalRootEntity} rootEntityType Filter by root entity type
     * @param {string} [rootEntityId] Optional filter by root entity ID (e.g., appointment ID)
     * @param {number} [page] Page number (0-based)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listSignals(rootEntityType: SignalRootEntity, rootEntityId?: string, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return SignalsApiFp(this.configuration).listSignals(rootEntityType, rootEntityId, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List signals where the current user is in the audience for the entity: - Behandelaar of the client (CLIENT, CARE_TRAJECTORY, APPOINTMENT, APPOINTMENT_FACT chains) - Directly the practitioner (PRACTITIONER root) - Org admin (sees all org signals)  Replaces the surface filtering that was previously expressed via the `context` query parameter on `/api/v1/signals`. Returns paginated results sorted by creation time (newest first).  The filter parameters narrow that audience set; none of them can widen it. A signal the audience rules exclude stays excluded whatever is filtered on — the filters are applied on top of the audience predicate, never instead of it. 
     * @summary List signals visible to the current user
     * @param {number} [page] Page number (0-based)
     * @param {number} [size] Page size
     * @param {string} [clientId] Only signals whose entity chain resolves to this client — rooted on the client itself or on its appointments, care trajectories, documents, form submissions and appointment facts. Signals rooted on organization-level entities (ORGANIZATION/LOCATION/PRACTITIONER/INSURER/CONTRACT) are not client-specific and never match. 
     * @param {Array<string>} [definitionKey] Only signals raised by one of these signal definitions. Repeatable; values combine as OR.
     * @param {Array<SignalSeverity>} [severity] Only signals of one of these severities. Repeatable; values combine as OR.
     * @param {Array<SignalRootEntity>} [rootEntityType] Only signals rooted on one of these entity types. Repeatable; values combine as OR.
     * @param {boolean} [blocksBilling] &#x60;true&#x60; for only the signals that hold back an invoice and a GDS801 export, &#x60;false&#x60; for only those that do not. Omit for both.  This is a different axis from &#x60;severity&#x60;, not a reading of it: severity says how loud a signal is, &#x60;blocksBilling&#x60; says whether it stops a declaration. Both are set independently per signal definition. 
     * @param {string} [search] Free-text match, case-insensitive, on the client the signal is about and on the signal\&#39;s own name and description.  The *rendered* message is deliberately not matched: it does not exist in the database — it is produced per request from the definition\&#39;s template and the signal\&#39;s cached context — so a server-side query cannot see it. What a user types is the client\&#39;s name or the wording of the signal type, and both of those are matched. 
     * @param {string} [sort] Sort order as &#x60;field,direction&#x60;. Supported fields: createdAt. Any other field falls back to createdAt. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listSignalsVisibleToMe(page?: number, size?: number, clientId?: string, definitionKey?: Array<string>, severity?: Array<SignalSeverity>, rootEntityType?: Array<SignalRootEntity>, blocksBilling?: boolean, search?: string, sort?: string, options?: RawAxiosRequestConfig) {
        return SignalsApiFp(this.configuration).listSignalsVisibleToMe(page, size, clientId, definitionKey, severity, rootEntityType, blocksBilling, search, sort, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The same audience set as `/api/v1/signals/visible-to-me` and the same filters, counted per signal definition instead of listed per occurrence.  A small number of causes multiplied across many rows is what a signal backlog actually is — one missing AGB-code across twelve behandelaars is hundreds of signals. Per occurrence that is noise; per definition it is a short worklist (#3830).  Each count is computed over the **whole** filtered set, not over a page of it: a count taken from a loaded page would be wrong the moment the list is paged, which is the one thing this endpoint exists to avoid. Expanding a group means calling `/api/v1/signals/visible-to-me` with the same filters plus that group\'s `definitionKey`, so the rows under a group are the rows it counted. 
     * @summary Count the signals visible to the current user, per signal definition
     * @param {string} [clientId] Only signals whose entity chain resolves to this client — rooted on the client itself or on its appointments, care trajectories, documents, form submissions and appointment facts. Signals rooted on organization-level entities (ORGANIZATION/LOCATION/PRACTITIONER/INSURER/CONTRACT) are not client-specific and never match. 
     * @param {Array<string>} [definitionKey] Only signals raised by one of these signal definitions. Repeatable; values combine as OR.
     * @param {Array<SignalSeverity>} [severity] Only signals of one of these severities. Repeatable; values combine as OR.
     * @param {Array<SignalRootEntity>} [rootEntityType] Only signals rooted on one of these entity types. Repeatable; values combine as OR.
     * @param {boolean} [blocksBilling] &#x60;true&#x60; for only the signals that hold back an invoice and a GDS801 export, &#x60;false&#x60; for only those that do not. Omit for both.  This is a different axis from &#x60;severity&#x60;, not a reading of it: severity says how loud a signal is, &#x60;blocksBilling&#x60; says whether it stops a declaration. Both are set independently per signal definition. 
     * @param {string} [search] Free-text match, case-insensitive, on the client the signal is about and on the signal\&#39;s own name and description.  The *rendered* message is deliberately not matched: it does not exist in the database — it is produced per request from the definition\&#39;s template and the signal\&#39;s cached context — so a server-side query cannot see it. What a user types is the client\&#39;s name or the wording of the signal type, and both of those are matched. 
     * @param {string} [sort] Sort order as &#x60;field,direction&#x60;. Supported fields: count, name. Any other field falls back to count. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public summarizeSignalsVisibleToMe(clientId?: string, definitionKey?: Array<string>, severity?: Array<SignalSeverity>, rootEntityType?: Array<SignalRootEntity>, blocksBilling?: boolean, search?: string, sort?: string, options?: RawAxiosRequestConfig) {
        return SignalsApiFp(this.configuration).summarizeSignalsVisibleToMe(clientId, definitionKey, severity, rootEntityType, blocksBilling, search, sort, options).then((request) => request(this.axios, this.basePath));
    }
}



